import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  totpStatusApi, totpEnrollStartApi, totpEnrollVerifyApi,
  totpDisableApi, totpRegenBackupCodesApi,
} from '../api/auth';

/** Render an otpauth:// URI as a QR code via a public chart endpoint as a fallback,
 *  but ideally we'd use a local QR library. For zero-deps, we embed a tiny SVG QR
 *  using the qr-server.com public API. If the user is offline they can still see
 *  the secret string to type in manually. */
const QRImage: React.FC<{ uri: string; size?: number }> = ({ uri, size = 192 }) => {
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(uri)}`;
  return <img src={src} alt="TOTP QR code" width={size} height={size} className="rounded-lg border border-gray-200" />;
};

const PreList: React.FC<{ items: string[] }> = ({ items }) => (
  <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm font-mono whitespace-pre-wrap">
    {items.join('\n')}
  </pre>
);

export const Security: React.FC = () => {
  const [params] = useSearchParams();
  const enrollFromQs = params.get('enroll') === '1';
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Enrollment state
  const [enrolling, setEnrolling] = useState(false);
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [enrollCode, setEnrollCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // Disable state
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [regenCode, setRegenCode] = useState('');
  const [showRegen, setShowRegen] = useState(false);

  const refresh = async () => {
    try {
      const s = await totpStatusApi();
      setStatus(s);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (status && !status.enabled && enrollFromQs && !enrolling) startEnroll();
  }, [status, enrollFromQs, enrolling]);

  const startEnroll = async () => {
    setError('');
    try {
      const r = await totpEnrollStartApi();
      setSecret(r.secret);
      setOtpauthUri(r.otpauth_uri);
      setEnrolling(true);
    } catch (e: any) { setError(e.message); }
  };

  const verifyEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const r = await totpEnrollVerifyApi(enrollCode.trim());
      setBackupCodes(r.backup_codes);
      setEnrolling(false);
      await refresh();
    } catch (e: any) { setError(e.message); }
  };

  const downloadBackupCodes = (codes: string[]) => {
    const text = `Intrlock — backup codes\nGenerated: ${new Date().toISOString()}\n\nEach code can be used once. Keep them somewhere safe.\n\n${codes.join('\n')}\n`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `intrlock-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const disable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await totpDisableApi(disableCode.trim());
      setDisableCode(''); setShowDisable(false);
      await refresh();
    } catch (e: any) { setError(e.message); }
  };

  const regen = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const r = await totpRegenBackupCodesApi(regenCode.trim());
      setBackupCodes(r.backup_codes);
      setRegenCode(''); setShowRegen(false);
      await refresh();
    } catch (e: any) { setError(e.message); }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading security settings…</div>;

  return (
    <div className="shadow-page-frame shadow-readable-form">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Security</h1>
      <p className="text-gray-500 mb-8">Two-factor authentication settings for your Intrlock account.</p>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {/* Status card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Authenticator App</h2>
            {status?.enabled ? (
              <p className="text-sm text-gray-500 mt-1">
                Enabled · {status.backup_codes_remaining} backup codes remaining · {status.trusted_devices_count} trusted device(s)
              </p>
            ) : (
              <p className="text-sm text-amber-700 mt-1">
                {status?.enrollment_required ? 'Required — please enroll to keep your account safe.' : 'Not enabled.'}
              </p>
            )}
          </div>
          <span className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${status?.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {status?.enabled ? 'On' : 'Off'}
          </span>
        </div>

        {!status?.enabled && !enrolling && (
          <button onClick={startEnroll} className="mt-4 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
            Set up two-factor
          </button>
        )}

        {enrolling && (
          <div className="mt-6 border-t border-gray-100 pt-6">
            <h3 className="font-semibold text-gray-900 mb-3">1. Scan with your authenticator app</h3>
            <p className="text-sm text-gray-500 mb-3">
              Use Google Authenticator, 1Password, Authy, or any RFC&nbsp;6238-compatible app.
            </p>
            <div className="flex items-start gap-6 mb-6">
              <QRImage uri={otpauthUri} />
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Can&apos;t scan? Enter this key manually:</p>
                <code className="block bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm font-mono break-all">{secret}</code>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-3">2. Enter the 6-digit code shown in the app</h3>
            <form onSubmit={verifyEnroll} className="flex items-center gap-3">
              <input
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="w-40 px-4 py-3 rounded-lg border border-gray-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 outline-none text-center text-lg tracking-widest font-mono"
                autoComplete="one-time-code"
                inputMode="numeric"
              />
              <button type="submit" disabled={enrollCode.length !== 6} className="px-4 py-3 rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-40 hover:bg-emerald-700">
                Verify &amp; enable
              </button>
              <button type="button" onClick={() => setEnrolling(false)} className="px-4 py-3 text-sm text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            </form>
          </div>
        )}

        {backupCodes && (
          <div className="mt-6 border-t border-gray-100 pt-6">
            <h3 className="font-semibold text-gray-900 mb-2">Backup codes</h3>
            <p className="text-sm text-gray-500 mb-3">
              Save these somewhere safe. Each one can be used once in place of a code from your authenticator app.
              You won&apos;t see them again.
            </p>
            <PreList items={backupCodes} />
            <div className="mt-3 flex gap-2">
              <button onClick={() => navigator.clipboard?.writeText(backupCodes.join('\n'))} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
                Copy
              </button>
              <button onClick={() => downloadBackupCodes(backupCodes)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
                Download .txt
              </button>
              <button onClick={() => setBackupCodes(null)} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
                I&apos;ve saved them
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Manage / Disable card — only when enabled */}
      {status?.enabled && !enrolling && !backupCodes && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Manage</h2>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowRegen(!showRegen)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
              Regenerate backup codes
            </button>
            <button onClick={() => setShowDisable(!showDisable)} className="px-3 py-1.5 rounded-lg border border-red-200 text-sm text-red-700 hover:bg-red-50">
              Disable two-factor
            </button>
          </div>

          {showRegen && (
            <form onSubmit={regen} className="mt-4 flex items-center gap-3">
              <input
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Current code"
                className="w-40 px-3 py-2 rounded-lg border border-gray-300 font-mono text-center"
              />
              <button type="submit" className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold">Regenerate</button>
            </form>
          )}

          {showDisable && (
            <form onSubmit={disable} className="mt-4 flex items-center gap-3">
              <input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Current code"
                className="w-40 px-3 py-2 rounded-lg border border-gray-300 font-mono text-center"
              />
              <button type="submit" className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold">Disable</button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};
